import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { KineticLines } from "../components/KineticLines";
import { Reveal } from "../components/Reveal";
import { Marquee } from "../components/Marquee";
import { SectionHead } from "../components/SectionHead";
import { EventRow } from "../components/EventRow";
import { InstagramReels } from "../components/InstagramReels";
import { WhatsAppCTA } from "../components/WhatsAppCTA";
import { useSiteContent } from "../hooks/useSiteContent";
import { MANIFESTO, IMAGES, CONTACTS } from "../data/content";

const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yImg = useTransform(scrollYProgress, [0, 1], [-45, 45]);
  const yLogo = useTransform(scrollYProgress, [0, 1], [0, 70]);

  return (
    <section ref={ref} data-testid="hero-section" className="relative overflow-hidden pt-28 md:pt-40">
      <div className="mx-auto grid max-w-[1400px] gap-14 px-5 pb-16 md:px-10 md:pb-24 lg:grid-cols-12 lg:gap-8">
        <div className="lg:col-span-7">
          <Reveal y={12}>
            <div className="mb-8 flex items-center gap-3">
              <span className="h-2.5 w-2.5 bg-orange" aria-hidden="true" />
              <span className="font-oswald text-xs uppercase tracking-[0.3em] text-navy md:text-sm">
                Movimento Civico · Trapani — Erice
              </span>
            </div>
          </Reveal>

          <KineticLines
            lines={["Ascolto", "Cura", <span key="s" className="text-orange">Solidarietà</span>]}
            className="font-oswald text-[clamp(3.4rem,10vw,8.75rem)] font-semibold uppercase leading-[0.92] tracking-tight"
          />

          <Reveal delay={0.6} className="mt-10 max-w-md">
            <p data-testid="hero-subtitle" className="text-base leading-relaxed opacity-80 md:text-lg">
              Siamo cittadini che amano la propria terra. Nessun partito alle spalle, nessuna bandiera se non una:
              costruire insieme il futuro di Trapani, Erice e — domani — di tutta la provincia.
            </p>
          </Reveal>

          <Reveal delay={0.75} className="mt-10 flex flex-wrap items-center gap-5">
            <Link
              to="/chi-siamo"
              data-testid="hero-cta-chi-siamo"
              className="inline-flex items-center gap-3 border border-ink bg-ink px-7 py-4 font-oswald text-sm font-medium uppercase tracking-widest text-bone transition-[background-color,color] duration-300 hover:bg-transparent hover:text-ink"
            >
              Scopri chi siamo <ArrowRight className="h-4 w-4" />
            </Link>
            <WhatsAppCTA testId="hero-whatsapp-button" />
          </Reveal>
        </div>

        <div className="relative lg:col-span-5">
          <Reveal delay={0.4} className="relative ml-auto w-full max-w-md lg:max-w-none">
            <div className="relative aspect-[3/4] overflow-hidden rounded-t-full border border-ink">
              <motion.img
                src={IMAGES.hero}
                alt="Il mulino delle saline di Trapani"
                style={{ y: yImg }}
                className="absolute -top-[15%] left-0 h-[130%] w-full object-cover"
              />
            </div>
            <motion.div
              style={{ y: yLogo }}
              className="absolute -bottom-8 -left-6 md:-left-12"
              animate={{ y: [0, -10, 0] }}
              transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
            >
              <div className="rounded-full border border-ink bg-bone p-3 shadow-[6px_6px_0_#0E1B33]">
                <img src="/logo.png" alt="Simbolo di Progetto Città" className="h-24 w-24 md:h-36 md:w-36" />
              </div>
            </motion.div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

const ManifestoSection = () => (
  <section id="manifesto" data-testid="manifesto-section" className="mx-auto max-w-[1400px] px-5 py-20 md:px-10 md:py-32">
    <SectionHead kicker="Il nostro manifesto" title={<>Quattro impegni,<br />una sola città.</>} />
    <div>
      {MANIFESTO.map((c, i) => (
        <Reveal key={c.num} delay={i * 0.06}>
          <div data-testid={`manifesto-chapter-${c.num}`} className="grid gap-6 border-t border-ink py-10 md:grid-cols-12 md:gap-8 md:py-14">
            <div className="md:col-span-3">
              <span className="font-playfair text-6xl font-bold italic leading-none text-orange md:text-8xl">{c.num}</span>
            </div>
            <h3 className="font-oswald text-3xl font-semibold uppercase tracking-tight md:col-span-4 md:text-5xl">
              {c.title}
            </h3>
            <p className="max-w-md text-sm leading-relaxed opacity-80 md:col-span-5 md:text-base">{c.text}</p>
          </div>
        </Reveal>
      ))}
    </div>
  </section>
);

const MembersTeaser = () => (
  <section data-testid="members-teaser-section" className="mx-auto max-w-[1400px] px-5 py-14 md:px-10 md:py-20">
    <div className="grid gap-10 md:grid-cols-12 md:items-end">
      <div className="md:col-span-7">
        <SectionHead kicker="Le persone" title={<>Facce, non simboli.<br />Tutte alla pari.</>} className="mb-0" />
      </div>
      <Reveal className="md:col-span-5">
        <p className="text-sm leading-relaxed opacity-80 md:text-base">
          Dietro Progetto Città ci sono cittadini con storie, competenze e ruoli diversi: nessuna vetrina
          e nessuna gerarchia. Li scopri tutti insieme, alla pari, nella pagina dedicata.
        </p>
        <Link
          to="/membri"
          data-testid="members-teaser-link"
          className="group mt-8 inline-flex items-center gap-3 border border-ink bg-ink px-7 py-4 font-oswald text-sm font-medium uppercase tracking-widest text-bone transition-[background-color,color] duration-300 hover:bg-transparent hover:text-ink"
        >
          Scopri tutti i membri <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
        </Link>
      </Reveal>
    </div>
  </section>
);

const EventsPreview = ({ events }) => (
  <section data-testid="events-preview-section" className="mx-auto max-w-[1400px] px-5 py-14 md:px-10 md:py-20">
    <div className="flex flex-wrap items-end justify-between gap-6">
      <SectionHead kicker="In agenda" title="Prossimi appuntamenti." className="mb-0" />
      <Reveal className="mb-2">
        <Link
          to="/eventi"
          data-testid="events-preview-link"
          className="group inline-flex items-center gap-2 font-oswald text-sm uppercase tracking-widest text-navy transition-colors duration-300 hover:text-orange"
        >
          Tutti gli eventi <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
        </Link>
      </Reveal>
    </div>
    {events.length > 0 ? (
      <div className="mt-14 border-t border-ink">
        {events.slice(0, 3).map((e, i) => (
          <EventRow key={i} event={e} index={i} />
        ))}
      </div>
    ) : (
      <Reveal className="mt-14">
        <div data-testid="events-empty-home" className="flex flex-col items-start gap-6 border border-ink p-8 md:flex-row md:items-center md:justify-between md:p-12">
          <div>
            <h3 className="font-oswald text-2xl font-semibold uppercase tracking-tight md:text-3xl">
              Stiamo preparando le <span className="text-orange">prime iniziative.</span>
            </h3>
            <p className="mt-3 max-w-lg text-sm leading-relaxed opacity-80">
              Assemblee, banchetti e giornate di cura del territorio sono in arrivo tra Trapani ed Erice.
              Scrivici su WhatsApp per restare aggiornato.
            </p>
          </div>
          <WhatsAppCTA
            testId="events-empty-whatsapp-button"
            label="Resta aggiornato"
            message="Ciao! Vorrei essere aggiornato sui prossimi eventi di Progetto Città."
          />
        </div>
      </Reveal>
    )}
  </section>
);

const CtaBand = () => (
  <section data-testid="cta-band-section" className="bg-navy text-bone">
    <div className="mx-auto max-w-[1400px] px-5 py-20 md:px-10 md:py-32">
      <Reveal>
        <div className="mb-8 flex items-center gap-3">
          <span className="h-2.5 w-2.5 bg-orange" aria-hidden="true" />
          <span className="font-oswald text-xs uppercase tracking-[0.3em] text-bone/70 md:text-sm">
            Domande e segnalazioni
          </span>
        </div>
      </Reveal>
      <Reveal delay={0.1}>
        <h2 className="max-w-5xl font-oswald text-4xl font-semibold uppercase leading-[0.95] tracking-tight md:text-7xl">
          Hai visto qualcosa che <span className="text-orange">non va?</span> Diccelo.
        </h2>
      </Reveal>
      <Reveal delay={0.2} className="mt-8 max-w-xl">
        <p className="text-base leading-relaxed text-bone/80">
          Una buca in strada, un'idea per il quartiere, un disservizio da segnalare: scrivici su WhatsApp,
          ti rispondiamo il prima possibile. Oppure mandaci una mail a{" "}
          <a href={`mailto:${CONTACTS.email}`} data-testid="cta-email-link" className="underline underline-offset-4 transition-colors duration-300 hover:text-orange">
            {CONTACTS.email}
          </a>
          .
        </p>
      </Reveal>
      <Reveal delay={0.3} className="mt-10">
        <WhatsAppCTA big testId="cta-whatsapp-button" label="Scrivici su WhatsApp" />
      </Reveal>
    </div>
  </section>
);

export default function Home() {
  const { events, settings } = useSiteContent();
  return (
    <>
      <Hero />
      <Marquee items={["Ascolto", "Cura", "Solidarietà", "Partecipazione", "Trasparenza", "Territorio"]} />
      <ManifestoSection />
      <Marquee dark items={["La città siamo noi", "Progetto Città", "Dal basso, insieme"]} testId="editorial-marquee-dark" />
      <MembersTeaser />
      <EventsPreview events={events} />
      <InstagramReels widgetId={settings?.instagram?.widgetId} instagramUrl={settings?.instagram?.profileUrl} />
      <CtaBand />
    </>
  );
}
